import { useCallback, useEffect, useRef, useState } from 'react';
import { baseHttp, trpc } from '../lib/trpc.js';
import { inteiro } from '../lib/formato.js';

interface RespostaUpload {
  loteId: string;
  arquivos: Array<{ id: string; nome: string; status: string }>;
}

const FORMATOS = ['NF-e 55', 'NFC-e 65', 'NFS-e Nacional', 'NFS-e ABRASF', 'CT-e 57/67'];

const ROTULO_ARQUIVO: Record<string, string> = {
  recebido: 'na fila',
  processando: 'processando',
  processado: 'ok',
  erro: 'erro',
};

export function Upload() {
  const utils = trpc.useUtils();
  const entrada = useRef<HTMLInputElement>(null);
  const [sobre, setSobre] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [loteId, setLoteId] = useState<string | null>(null);

  const status = trpc.lotes.status.useQuery(
    { loteId: loteId ?? '' },
    {
      enabled: loteId !== null,
      refetchInterval: (query) => {
        const dados = query.state.data;
        if (!dados) return false;
        const loteAberto = dados.lote.status === 'aberto' || dados.lote.status === 'processando';
        // O reprocessamento reenfileira arquivos sem mexer no status do lote,
        // então a fila dos arquivos também precisa ser olhada — senão a barra
        // congela justamente na retentativa.
        const arquivoNaFila = dados.arquivos.some(
          (a) => a.status === 'recebido' || a.status === 'processando',
        );
        return loteAberto || arquivoNaFila ? 1500 : false;
      },
    },
  );

  const reprocessar = trpc.lotes.reprocessar.useMutation({
    onSuccess: () => {
      void utils.lotes.status.invalidate();
    },
  });

  const dados = status.data ?? null;
  const terminalAnterior = useRef<string | null>(null);

  useEffect(() => {
    const s = dados?.lote.status;
    if (s !== 'concluido' && s !== 'concluido_com_erro') {
      terminalAnterior.current = null;
      return;
    }
    if (terminalAnterior.current === s) return;
    terminalAnterior.current = s;
    void utils.resumo.invalidate();
    void utils.documentos.invalidate();
    void utils.obras.invalidate();
    void utils.alertas.invalidate();
  }, [dados?.lote.status, utils]);

  const enviar = useCallback(async (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;
    setErroEnvio(null);
    setEnviando(true);
    try {
      const corpo = new FormData();
      for (const arquivo of Array.from(lista)) corpo.append('arquivos', arquivo);
      // Upload não passa pelo tRPC: é multipart direto na rota HTTP da API.
      const resposta = await fetch(`${baseHttp}/api/upload`, {
        method: 'POST',
        body: corpo,
        credentials: 'include',
      });
      if (!resposta.ok) {
        throw new Error(`A API respondeu ${resposta.status} ao receber os arquivos.`);
      }
      const json = (await resposta.json()) as RespostaUpload;
      setLoteId(json.loteId);
    } catch (e) {
      setErroEnvio(e instanceof Error ? e.message : 'Falha ao enviar os arquivos.');
    } finally {
      setEnviando(false);
    }
  }, []);

  const total = dados?.lote.totalArquivos ?? 0;
  const processados = dados?.lote.processados ?? 0;
  const comErro = dados?.lote.comErro ?? 0;
  const emAndamento = dados !== null && (dados.lote.status === 'aberto' || dados.lote.status === 'processando');
  const compacto = enviando || dados !== null;

  return (
    <>
      <div
        className={`drop${compacto ? ' compact' : ''}${sobre ? ' over' : ''}`}
        role="button"
        tabIndex={0}
        aria-label="Selecionar arquivos XML"
        aria-busy={enviando || emAndamento}
        onClick={() => entrada.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            entrada.current?.click();
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setSobre(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setSobre(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setSobre(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setSobre(false);
          void enviar(e.dataTransfer?.files ?? null);
        }}
      >
        <div className="grow">
          <h2>
            {enviando
              ? 'Enviando arquivos…'
              : dados
                ? `${inteiro(processados)} de ${inteiro(total)} arquivo(s) processado(s)`
                : 'Arraste os XMLs aqui'}
          </h2>
          <p>
            {dados
              ? 'Solte mais XMLs ou .zip para somar à análise.'
              : 'ou clique para selecionar. Aceita .xml e .zip — o processamento acontece no servidor da sua empresa.'}
          </p>
          {dados ? (
            <div
              className="progresso"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total || 1}
              aria-valuenow={processados}
              aria-label="Progresso do lote"
            >
              <i style={{ width: `${total > 0 ? (processados / total) * 100 : 0}%` }} />
            </div>
          ) : null}
        </div>

        <div className="formats">
          {FORMATOS.map((f) => (
            <span className="chip" key={f}>
              {f}
            </span>
          ))}
        </div>

        {dados && dados.arquivos.length > 0 ? (
          <div className="files">
            {dados.arquivos.slice(0, 24).map((a) => (
              <span
                key={a.id}
                className={`fbadge${a.status === 'erro' ? ' err' : a.status === 'processado' ? ' ok' : ''}`}
                title={a.erro ?? undefined}
              >
                {a.nome} · {ROTULO_ARQUIVO[a.status] ?? a.status}
                {a.status === 'processado' && a.documentosGerados > 0
                  ? ` · ${inteiro(a.documentosGerados)} doc`
                  : ''}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <input
        ref={entrada}
        type="file"
        multiple
        accept=".xml,.zip,text/xml,application/xml,application/zip"
        style={{ display: 'none' }}
        onChange={(e) => {
          void enviar(e.target.files);
          e.target.value = '';
        }}
      />

      {erroEnvio ? (
        <div className="erro-box" role="alert" style={{ marginBottom: 12 }}>
          {erroEnvio}
        </div>
      ) : null}

      {dados && comErro > 0 ? (
        <div className="toolbar" style={{ marginTop: -4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--crit)' }}>
            {inteiro(comErro)} arquivo(s) não puderam ser lidos.
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={reprocessar.isPending || emAndamento}
            onClick={() => {
              if (loteId) reprocessar.mutate({ loteId });
            }}
          >
            {reprocessar.isPending ? 'Reenfileirando…' : 'Reprocessar com erro'}
          </button>
        </div>
      ) : null}
    </>
  );
}
